import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { schedulingLinksApi, SchedulingLink, SchedulingLinkFormData } from '../services/api';
import {
    Link as LinkIcon,
    Plus,
    Copy,
    Check,
    Clock,
    Calendar,
    Settings,
    ToggleLeft,
    ToggleRight,
    Trash2,
    Edit,
    Users,
    ExternalLink,
    X,
    Ban,
    CalendarX,
    Zap,
    ClipboardList
} from 'lucide-react';

const DAYS_OF_WEEK = [
    { value: null, label: 'Every day' },
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

const TIMES = Array.from({ length: 24 }, (_, h) => {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const period = h < 12 ? 'AM' : 'PM';
    return { value: `${h.toString().padStart(2, '0')}:00:00`, label: `${hour12}:00 ${period}` };
});

const DURATIONS = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 45, label: '45 minutes' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
];

export function SchedulingLinksPage() {
    const [links, setLinks] = useState<SchedulingLink[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingLink, setEditingLink] = useState<SchedulingLink | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    useEffect(() => {
        loadLinks();
    }, []);

    const loadLinks = async () => {
        try {
            const data = await schedulingLinksApi.list();
            setLinks(data);
        } catch (err) {
            console.error('Failed to load scheduling links:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyLink = async (link: SchedulingLink) => {
        try {
            await navigator.clipboard.writeText(link.booking_url);
            setCopiedId(link.id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleToggle = async (link: SchedulingLink) => {
        try {
            await schedulingLinksApi.toggle(link.id);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to toggle link');
        }
    };

    const handleDelete = async (link: SchedulingLink) => {
        if (!confirm(`Delete "${link.name}"? This cannot be undone.`)) return;
        try {
            await schedulingLinksApi.delete(link.id);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to delete link');
        }
    };

    const handleCreateOrUpdate = async (data: SchedulingLinkFormData) => {
        try {
            if (editingLink) {
                await schedulingLinksApi.update(editingLink.id, data);
            } else {
                await schedulingLinksApi.create(data);
            }
            setShowCreateModal(false);
            setEditingLink(null);
            loadLinks();
        } catch (err: any) {
            alert(err.message || 'Failed to save');
        }
    };

    if (isLoading) {
        return (
            <div className="page container">
                <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    return (
        <div className="page container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-6)' }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <LinkIcon size={28} />
                        Scheduling Links
                    </h1>
                    <p style={{ color: 'var(--color-gray-500)', marginTop: 'var(--space-1)' }}>
                        Create shareable links for others to propose meeting times
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary"
                >
                    <Plus size={18} />
                    Create Link
                </button>
            </div>

            {links.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                    <LinkIcon size={48} style={{ color: 'var(--color-gray-300)', margin: '0 auto var(--space-4)' }} />
                    <h3>No scheduling links yet</h3>
                    <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-4)' }}>
                        Create a scheduling link to let others propose meeting times based on your calendar availability.
                    </p>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="btn btn-primary"
                    >
                        <Plus size={18} />
                        Create Your First Link
                    </button>
                </div>
            ) : (
                <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                    {links.map(link => (
                        <div
                            key={link.id}
                            className="card"
                            style={{
                                opacity: link.is_active ? 1 : 0.6,
                                borderLeft: `4px solid ${link.is_active ? 'var(--color-primary)' : 'var(--color-gray-300)'}`
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                        <h3 style={{ margin: 0 }}>{link.name}</h3>
                                        <span style={{
                                            fontSize: 'var(--font-size-xs)',
                                            background: link.booking_type === 'direct' ? '#dcfce7' : 'var(--color-gray-100)',
                                            color: link.booking_type === 'direct' ? '#16a34a' : 'var(--color-gray-600)',
                                            padding: '2px 8px',
                                            borderRadius: 'var(--radius-full)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            {link.booking_type === 'direct' ? <Zap size={10} /> : <ClipboardList size={10} />}
                                            {link.booking_type === 'direct' ? 'Instant' : 'Propose'}
                                        </span>
                                        {!link.is_active && (
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                background: 'var(--color-gray-200)',
                                                padding: '2px 8px',
                                                borderRadius: 'var(--radius-full)'
                                            }}>
                                                Inactive
                                            </span>
                                        )}
                                        {link.pending_count > 0 && (
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                background: 'var(--color-primary)',
                                                color: 'white',
                                                padding: '2px 8px',
                                                borderRadius: 'var(--radius-full)',
                                                fontWeight: 600
                                            }}>
                                                {link.pending_count} pending
                                            </span>
                                        )}
                                    </div>

                                    {link.description && (
                                        <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-3)' }}>
                                            {link.description}
                                        </p>
                                    )}

                                    <div style={{ display: 'flex', gap: 'var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-gray-500)' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Clock size={14} />
                                            {link.duration_minutes} min
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Calendar size={14} />
                                            {link.advance_days_min}-{link.advance_days_max} days out
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Users size={14} />
                                            {link.total_proposals} total proposals
                                        </span>
                                    </div>

                                    <div style={{
                                        marginTop: 'var(--space-3)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'var(--color-gray-100)',
                                        borderRadius: 'var(--radius-sm)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        fontSize: 'var(--font-size-sm)'
                                    }}>
                                        <code style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {link.booking_url}
                                        </code>
                                        <button
                                            onClick={() => handleCopyLink(link)}
                                            className="btn btn-sm btn-secondary"
                                            style={{ flexShrink: 0 }}
                                        >
                                            {copiedId === link.id ? <Check size={14} /> : <Copy size={14} />}
                                            {copiedId === link.id ? 'Copied!' : 'Copy'}
                                        </button>
                                        <a
                                            href={link.booking_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-secondary"
                                            style={{ flexShrink: 0 }}
                                        >
                                            <ExternalLink size={14} />
                                        </a>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 'var(--space-2)', marginLeft: 'var(--space-4)' }}>
                                    <button
                                        onClick={() => handleToggle(link)}
                                        className="btn btn-sm btn-secondary"
                                        title={link.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                        {link.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingLink(link);
                                            setShowCreateModal(true);
                                        }}
                                        className="btn btn-sm btn-secondary"
                                        title="Edit"
                                    >
                                        <Edit size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(link)}
                                        className="btn btn-sm btn-secondary"
                                        title="Delete"
                                        style={{ color: 'var(--color-error)' }}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>

                            {link.pending_count > 0 && (
                                <Link
                                    to={`/proposals?link_id=${link.id}`}
                                    style={{
                                        display: 'block',
                                        marginTop: 'var(--space-3)',
                                        padding: 'var(--space-2)',
                                        background: 'var(--color-primary-light)',
                                        borderRadius: 'var(--radius-sm)',
                                        textAlign: 'center',
                                        color: 'var(--color-primary-hover)',
                                        fontWeight: 500,
                                        fontSize: 'var(--font-size-sm)'
                                    }}
                                >
                                    View {link.pending_count} pending proposal{link.pending_count !== 1 ? 's' : ''}
                                </Link>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <LinkFormModal
                    link={editingLink}
                    onClose={() => {
                        setShowCreateModal(false);
                        setEditingLink(null);
                    }}
                    onSubmit={handleCreateOrUpdate}
                />
            )}
        </div>
    );
}

// Link Form Modal Component
function LinkFormModal({
    link,
    onClose,
    onSubmit
}: {
    link: SchedulingLink | null;
    onClose: () => void;
    onSubmit: (data: SchedulingLinkFormData) => Promise<void>;
}) {
    const [name, setName] = useState(link?.name || '');
    const [description, setDescription] = useState(link?.description || '');
    const [bookingType, setBookingType] = useState<'proposal' | 'direct'>(link?.booking_type || 'proposal');
    const [durationMinutes, setDurationMinutes] = useState(link?.duration_minutes || 30);
    const [bufferBefore, setBufferBefore] = useState(link?.buffer_before || 0);
    const [bufferAfter, setBufferAfter] = useState(link?.buffer_after || 0);
    const [advanceDaysMin, setAdvanceDaysMin] = useState(link?.advance_days_min || 1);
    const [advanceDaysMax, setAdvanceDaysMax] = useState(link?.advance_days_max || 28);
    const [maxProposals, setMaxProposals] = useState(link?.max_proposals || 3);
    const [isSaving, setIsSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showBlocking, setShowBlocking] = useState(false);

    // Blocked dates state
    const [blockedDates, setBlockedDates] = useState<{ blocked_date: string; reason?: string }[]>(
        link?.blocked_dates?.map(d => ({ blocked_date: d.blocked_date, reason: d.reason || undefined })) || []
    );
    const [newBlockedDate, setNewBlockedDate] = useState('');
    const [newBlockedDateReason, setNewBlockedDateReason] = useState('');

    // Blocked times state
    const [blockedTimes, setBlockedTimes] = useState<{ day_of_week: number | null; start_time: string; end_time: string; reason?: string }[]>(
        link?.blocked_times?.map(t => ({ 
            day_of_week: t.day_of_week, 
            start_time: t.start_time, 
            end_time: t.end_time, 
            reason: t.reason || undefined 
        })) || []
    );
    const [newTimeDay, setNewTimeDay] = useState<number | null>(null);
    const [newTimeStart, setNewTimeStart] = useState('12:00:00');
    const [newTimeEnd, setNewTimeEnd] = useState('13:00:00');
    const [newTimeReason, setNewTimeReason] = useState('');

    const addBlockedDate = () => {
        if (!newBlockedDate) return;
        if (blockedDates.some(d => d.blocked_date === newBlockedDate)) {
            alert('This date is already blocked');
            return;
        }
        setBlockedDates([...blockedDates, { blocked_date: newBlockedDate, reason: newBlockedDateReason || undefined }]);
        setNewBlockedDate('');
        setNewBlockedDateReason('');
    };

    const removeBlockedDate = (index: number) => {
        setBlockedDates(blockedDates.filter((_, i) => i !== index));
    };

    const addBlockedTime = () => {
        if (newTimeStart >= newTimeEnd) {
            alert('End time must be after start time');
            return;
        }
        setBlockedTimes([...blockedTimes, { 
            day_of_week: newTimeDay, 
            start_time: newTimeStart, 
            end_time: newTimeEnd,
            reason: newTimeReason || undefined
        }]);
        setNewTimeReason('');
    };

    const removeBlockedTime = (index: number) => {
        setBlockedTimes(blockedTimes.filter((_, i) => i !== index));
    };

    const formatTime = (time: string) => {
        const [h] = time.split(':');
        const hour = parseInt(h);
        const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour < 12 ? 'AM' : 'PM';
        return `${hour12}:00 ${period}`;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            alert('Please enter a name for the link');
            return;
        }

        setIsSaving(true);
        try {
            await onSubmit({
                name: name.trim(),
                description: description.trim() || undefined,
                booking_type: bookingType,
                duration_minutes: durationMinutes,
                buffer_before: bufferBefore,
                buffer_after: bufferAfter,
                advance_days_min: advanceDaysMin,
                advance_days_max: advanceDaysMax,
                max_proposals: maxProposals,
                blocked_dates: blockedDates,
                blocked_times: blockedTimes
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-4)'
        }}>
            <div className="card" style={{ maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                    <h2 style={{ margin: 0 }}>{link ? 'Edit' : 'Create'} Scheduling Link</h2>
                    <button onClick={onClose} className="btn btn-secondary btn-sm">
                        <X size={18} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label className="form-label">Link Name *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g., 30-min Discovery Call"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Description (optional)</label>
                        <textarea
                            className="form-textarea"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Brief description shown to requesters"
                            rows={2}
                        />
                    </div>

                    {/* Booking Type Selector */}
                    <div className="form-group">
                        <label className="form-label">Booking Mode</label>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-2)' }}>
                            <button
                                type="button"
                                onClick={() => setBookingType('proposal')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    border: `2px solid ${bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: bookingType === 'proposal' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <ClipboardList size={24} style={{ color: bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontWeight: 600, color: bookingType === 'proposal' ? 'var(--color-primary)' : 'var(--color-gray-700)' }}>
                                    Propose Times
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', textAlign: 'center' }}>
                                    Invitees suggest times for you to approve
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setBookingType('direct')}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    border: `2px solid ${bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-200)'}`,
                                    borderRadius: 'var(--radius-md)',
                                    background: bookingType === 'direct' ? 'var(--color-primary-light)' : 'white',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <Zap size={24} style={{ color: bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-400)' }} />
                                <span style={{ fontWeight: 600, color: bookingType === 'direct' ? 'var(--color-primary)' : 'var(--color-gray-700)' }}>
                                    Instant Booking
                                </span>
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)', textAlign: 'center' }}>
                                    Invitees book directly with Teams meeting
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Duration</label>
                        <select
                            className="form-select"
                            value={durationMinutes}
                            onChange={(e) => setDurationMinutes(Number(e.target.value))}
                        >
                            {DURATIONS.map(d => (
                                <option key={d.value} value={d.value}>{d.label}</option>
                            ))}
                        </select>
                    </div>

                    {bookingType === 'proposal' && (
                        <div className="form-group">
                            <label className="form-label">Max time preferences per request</label>
                            <select
                                className="form-select"
                                value={maxProposals}
                                onChange={(e) => setMaxProposals(Number(e.target.value))}
                            >
                                {[1, 2, 3, 4, 5].map(n => (
                                    <option key={n} value={n}>
                                        {n} time{n > 1 ? 's' : ''}
                                    </option>
                                ))}
                            </select>
                            <p className="form-hint">
                                How many time options can requesters propose
                            </p>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Settings size={14} />
                        {showAdvanced ? 'Hide' : 'Show'} advanced settings
                    </button>

                    {showAdvanced && (
                        <>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                <div className="form-group">
                                    <label className="form-label">Availability starts</label>
                                    <select
                                        className="form-select"
                                        value={advanceDaysMin}
                                        onChange={(e) => setAdvanceDaysMin(Number(e.target.value))}
                                    >
                                        <option value={0}>Today</option>
                                        <option value={1}>Tomorrow</option>
                                        <option value={2}>In 2 days</option>
                                        <option value={3}>In 3 days</option>
                                        <option value={7}>In 1 week</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Availability ends</label>
                                    <select
                                        className="form-select"
                                        value={advanceDaysMax}
                                        onChange={(e) => setAdvanceDaysMax(Number(e.target.value))}
                                    >
                                        <option value={7}>1 week out</option>
                                        <option value={14}>2 weeks out</option>
                                        <option value={21}>3 weeks out</option>
                                        <option value={28}>4 weeks out</option>
                                        <option value={60}>2 months out</option>
                                        <option value={90}>3 months out</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                                <div className="form-group">
                                    <label className="form-label">Buffer before meeting</label>
                                    <select
                                        className="form-select"
                                        value={bufferBefore}
                                        onChange={(e) => setBufferBefore(Number(e.target.value))}
                                    >
                                        <option value={0}>None</option>
                                        <option value={5}>5 minutes</option>
                                        <option value={10}>10 minutes</option>
                                        <option value={15}>15 minutes</option>
                                        <option value={30}>30 minutes</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Buffer after meeting</label>
                                    <select
                                        className="form-select"
                                        value={bufferAfter}
                                        onChange={(e) => setBufferAfter(Number(e.target.value))}
                                    >
                                        <option value={0}>None</option>
                                        <option value={5}>5 minutes</option>
                                        <option value={10}>10 minutes</option>
                                        <option value={15}>15 minutes</option>
                                        <option value={30}>30 minutes</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Blocking Rules Section */}
                    <button
                        type="button"
                        onClick={() => setShowBlocking(!showBlocking)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-primary)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: 'var(--space-4)',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <Ban size={14} />
                        {showBlocking ? 'Hide' : 'Show'} blocking rules
                        {(blockedDates.length > 0 || blockedTimes.length > 0) && (
                            <span style={{
                                background: 'var(--color-primary)',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginLeft: '4px'
                            }}>
                                {blockedDates.length + blockedTimes.length}
                            </span>
                        )}
                    </button>

                    {showBlocking && (
                        <div style={{ 
                            background: 'var(--color-gray-50)', 
                            padding: 'var(--space-4)', 
                            borderRadius: 'var(--radius-md)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            {/* Blocked Dates */}
                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                    <CalendarX size={16} />
                                    Blocked Dates
                                </h4>
                                <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                    Block specific dates (e.g., vacation, holidays)
                                </p>

                                {blockedDates.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                        {blockedDates.map((d, i) => (
                                            <span
                                                key={i}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    background: 'white',
                                                    border: '1px solid var(--color-gray-200)',
                                                    borderRadius: 'var(--radius-full)',
                                                    padding: '4px 8px 4px 12px',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                {d.blocked_date}
                                                {d.reason && <span style={{ color: 'var(--color-gray-500)' }}>({d.reason})</span>}
                                                <button
                                                    type="button"
                                                    onClick={() => removeBlockedDate(i)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-gray-400)' }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="date"
                                            className="form-input"
                                            value={newBlockedDate}
                                            onChange={(e) => setNewBlockedDate(e.target.value)}
                                            min={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={newBlockedDateReason}
                                            onChange={(e) => setNewBlockedDateReason(e.target.value)}
                                            placeholder="Reason (optional)"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addBlockedDate}
                                        className="btn btn-secondary btn-sm"
                                        disabled={!newBlockedDate}
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Blocked Times */}
                            <div>
                                <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--space-2)' }}>
                                    <Clock size={16} />
                                    Blocked Time Periods
                                </h4>
                                <p className="form-hint" style={{ marginBottom: 'var(--space-3)' }}>
                                    Block recurring times (e.g., lunch hour, focus time)
                                </p>

                                {blockedTimes.length > 0 && (
                                    <div style={{ marginBottom: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                        {blockedTimes.map((t, i) => (
                                            <div
                                                key={i}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: 'white',
                                                    border: '1px solid var(--color-gray-200)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    padding: 'var(--space-2) var(--space-3)',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                            >
                                                <span>
                                                    <strong>{t.day_of_week === null ? 'Every day' : DAYS_OF_WEEK.find(d => d.value === t.day_of_week)?.label}</strong>
                                                    {' '}from {formatTime(t.start_time)} to {formatTime(t.end_time)}
                                                    {t.reason && <span style={{ color: 'var(--color-gray-500)' }}> ({t.reason})</span>}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeBlockedTime(i)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-gray-400)' }}
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Day</label>
                                        <select
                                            className="form-select"
                                            value={newTimeDay === null ? '' : newTimeDay}
                                            onChange={(e) => setNewTimeDay(e.target.value === '' ? null : Number(e.target.value))}
                                        >
                                            {DAYS_OF_WEEK.map(d => (
                                                <option key={d.label} value={d.value === null ? '' : d.value}>{d.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>From</label>
                                        <select
                                            className="form-select"
                                            value={newTimeStart}
                                            onChange={(e) => setNewTimeStart(e.target.value)}
                                        >
                                            {TIMES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>To</label>
                                        <select
                                            className="form-select"
                                            value={newTimeEnd}
                                            onChange={(e) => setNewTimeEnd(e.target.value)}
                                        >
                                            {TIMES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="form-label" style={{ fontSize: 'var(--font-size-xs)' }}>Reason</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={newTimeReason}
                                            onChange={(e) => setNewTimeReason(e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addBlockedTime}
                                        className="btn btn-secondary btn-sm"
                                    >
                                        <Plus size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary"
                            style={{ flex: 1 }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="btn btn-primary"
                            style={{ flex: 1 }}
                        >
                            {isSaving ? 'Saving...' : (link ? 'Update' : 'Create')} Link
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
